import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useFunction from '../../../hooks/useFunction';
import { useNavigate, useParams } from 'react-router-dom';
import FunctionOverview from './FunctionOverview';
import InvocationSourcesTable from './InvocationSourcesTable';
import ErrorsTable from '../../../components/ErrorsTable/ErrorsTable';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import useTimeFrame from '../../../hooks/useTimeFrame';
import useEnvironment from '../../../hooks/useEnvironment';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import useAnalytics from '../../../hooks/useAnalytics';
import {
  Row,
  FunctionBehaviorChartContainer,
  WebviewFlowChartHeader,
  CenterLoader,
  TitleModuleLabel,
  FlowChartContainer,
} from './FunctionPage.styles';
import { Row as RowBase } from '@hud/web-components/Layout/Layout';
import Page from '../../../components/Page/Page';
import { getFileName } from '../../../utils';
import { FunctionFlowChart } from '@hud/web-components/Hud/FunctionFlowChart/FunctionFlowChart';
import { showTintingConfig } from '@hud/web-components/Hud/FunctionFlowChart/configs';
import FlowFilterRenderer, {
  ServiceAndFlow,
  hashServiceAndFlow,
  unHashServiceAndFlow,
} from '../../../components/FlowFilterRenderer/FlowFilterRenderer';
import FunctionBehaviorChart from '@hud/web-components/Hud/FunctionBehaviorChart/FunctionBehaviorChart';
import { NodeData } from '@hud/web-components/Charts/FlowChart/types';
import { Node } from 'reactflow';
import { useFunctionSliceQueryString } from '../../../hooks/useFunctionFilter';
import { Container } from '@hud/web-components/Container/Container';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import { useQueryParamState } from '../../../hooks/useQueryParamState';
import FlowsByFunctionTable from './FlowsTable';
import { TagRecordSchema } from '@hud/portal-dtos/webapp-common.dto';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import IDESelectorRenderer from './IDESelectorRenderer';
import { isEndpoint, isKafkaTopic } from '@hud/portal-dtos/webapp-function.dto';
import { useBooleanFeatureFlag } from '../../../hooks/useFeatureFlags';

type FunctionPageQueryParams = {
  service: string;
  flow: string;
  tags: string[];
};

const CALL_GRAPH_NODE_WIDTH = '20vw';

export default function FunctionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const functionFilters = useFunctionSliceQueryString();
  const { timeFrame, setTimeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const { id } = useParams<{ id: string }>();
  const analytics = useAnalytics();
  const showDeployments = useBooleanFeatureFlag('show-deployments', true);
  const [functionIdPageLoaded, setFunctionIdPageLoaded] = useState('');

  const [pageState, setPageState, resetPageState] =
    useQueryParamState<FunctionPageQueryParams>({
      service: '',
      flow: '',
      tags: [],
    });

  const flow = pageState.flow;
  const service = pageState.service;
  const serviceAndFlowHash = hashServiceAndFlow(service, flow);

  const setServiceAndFlow = (serviceAndFlowHash: string | null) => {
    if (serviceAndFlowHash) {
      const serviceAndFlow: ServiceAndFlow =
        unHashServiceAndFlow(serviceAndFlowHash);
      setPageState(
        {
          service: serviceAndFlow.service,
          flow: serviceAndFlow.flow,
        },
        { state: location.state },
      );
      analytics.capture('filter_flow_selected', {
        service_id: Number.parseInt(serviceAndFlow.service),
        flow_id: Number.parseInt(serviceAndFlow.flow),
        function_id: Number.parseInt(id as string),
      });
    } else {
      resetPageState(['service', 'flow'], { state: location.state });
      analytics.capture('filter_flow_unselected', {
        function_id: Number.parseInt(id as string),
      });
    }
  };

  const defaultBreadcrumbs = [
    { title: 'Functions', to: '/explore/functions', nowrap: true },
  ];

  const parsedTags = TagRecordSchema.safeParse(pageState.tags);
  if (!parsedTags.success) {
    console.error(parsedTags.error.format());
  }
  const tags = parsedTags.data;

  const functionQuery = useFunction(parseInt(id as string), {
    timeFrame,
    environmentName: environment?.name,
    ...(flow && { flowId: parseInt(flow) }),
    ...(service && { serviceId: parseInt(service) }),
    tags,
  });

  useEffect(() => {
    if (functionQuery.isSuccess) {
      const fileName = getFileName(functionQuery.data.function.filePath);
      document.title = `${functionQuery.data?.function.signature} • ${fileName} • Hud`;
    } else {
      document.title = 'Hud';
    }
  }, [
    functionQuery.isSuccess,
    functionQuery.data?.function.signature,
    functionQuery.data?.function.filePath,
  ]);

  useEffect(() => {
    if (functionQuery.isSuccess && functionIdPageLoaded !== id) {
      analytics.capture('function_viewed', {
        function_id: Number.parseInt(id as string),
        function_name: functionQuery.data.function.name,
        environment: environment?.name,
      });
      setFunctionIdPageLoaded(id as string);
    }
  }, [
    functionQuery.isSuccess,
    functionQuery.data,
    id,
    functionIdPageLoaded,
    analytics,
    environment?.name,
  ]);

  const callerCount = useMemo(() => {
    const functionCallersCount =
      functionQuery.data?.callers.filter((c) => c.id).length ?? 0;
    const directFlowsCount =
      functionQuery.data?.directFlows
        .map((f) =>
          isEndpoint(f) ? f.methods.length : isKafkaTopic(f) ? 1 : 0,
        )
        .reduce((a, b) => a + b, 0) ?? 0;
    return functionCallersCount + directFlowsCount;
  }, [functionQuery.data]);

  if (functionQuery.isError) {
    return (
      <Page
        header={defaultBreadcrumbs}
        actions={
          <ActionsContainer>
            <EnvironmentMenu />
            <TimeFrameMenu />
            <ThemeButton />
          </ActionsContainer>
        }
      >
        {functionQuery.error.response?.status === 404
          ? functionQuery.error.response?.data.message
          : 'Something went wrong'}
      </Page>
    );
  }

  function navigateToNode(node: Node<NodeData>) {
    if (node.data.function?.type === 'FLOW') {
      if (node.data.function.flowType === 'ENDPOINT') {
        const endpointId = node.id.slice(1).replace('_', '-');
        navigate(`/explore/endpoints/${endpointId}?${functionFilters}`);
      }
      if (node.data.function.flowType === 'QUEUE') {
        const queueId = node.data.function.id;
        navigate(
          `/explore/messageQueues/${queueId}?service=${
            node.data.function.serviceId
          }&${functionFilters}`,
        );
      }
      return;
    }
    navigate(`/explore/functions/${node.id.slice(1)}?${functionFilters}`);
  }

  const referralBreadcrumbs: BreadcrumbItem[] | null =
    location.state?.referralBreadcrumbs;
  const baseBreadcrumbs = referralBreadcrumbs || defaultBreadcrumbs;

  const showExistsInFlow =
    serviceAndFlowHash === '' || serviceAndFlowHash === null;

  function getFunctionTitle() {
    const thirdPartyModule = functionQuery.data?.function.thirdPartyModule;
    const pageTitles: BreadcrumbItem[] = [];
      if (functionQuery.isSuccess) {
        if (thirdPartyModule) {
          pageTitles.push({
            title: <TitleModuleLabel module={thirdPartyModule}/>
          });
        }
        pageTitles.push({ title: functionQuery.data.function.signature });
      }
    return pageTitles;
  }

  return (
    <Page
      headerActions={
        functionQuery.isSuccess ? (
          <IDESelectorRenderer functionData={functionQuery.data.function} />
        ) : null
      }
      header={
        functionQuery.isLoading
          ? 'loading'
          : [
              ...baseBreadcrumbs,
              ...getFunctionTitle(),
            ]
      }
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <>
        <FlowFilterRenderer
          selectedFlow={serviceAndFlowHash}
          onSelectFlow={setServiceAndFlow}
          onClear={() => setServiceAndFlow(null)}
          flows={functionQuery.data?.flows}
          loading={functionQuery.isLoading}
        />
        <Row>
          <FunctionOverview
            function={functionQuery.data?.function}
            loading={functionQuery.isLoading}
          />
        </Row>
        <RowBase>
          <Container
            variant="filled"
            title="Call Graph"
            style={{ width: '100%', minHeight: '150px', zIndex: 10 }}
            icon="webviewMenu.flow"
          >
            {functionQuery.isLoading && (
              <CenterLoader>
                <Skeleton width={300} height={30} />
              </CenterLoader>
            )}
            {functionQuery.data && (
              <FlowChartContainer>
                <WebviewFlowChartHeader
                  callers={callerCount}
                  callees={functionQuery.data.callees.length}
                  nodeWidth={CALL_GRAPH_NODE_WIDTH}
                  hideTitleIfEmpty
                />
                <FunctionFlowChart
                  key={functionQuery.data.function.id}
                  width={1100}
                  height={800}
                  functionData={functionQuery.data}
                  useDynamicWidth
                  onNodeClick={navigateToNode}
                  compact
                  showTinting={showTintingConfig}
                  options={{
                    alignCenterNodeToTop: true,
                    clickableEndpoints: true,
                    clickableTopics: true,
                    nodeWidth: CALL_GRAPH_NODE_WIDTH,
                    nodeCap: 5,
                  }}
                />
              </FlowChartContainer>
            )}
          </Container>
        </RowBase>
        <Row>
          <FunctionBehaviorChartContainer
            title="Behavior"
            variant="filled"
            icon="webviewMenu.chart"
          >
            <FunctionBehaviorChart
              function={functionQuery.data?.function}
              graph={functionQuery.data?.graphs}
              deployments={showDeployments ? functionQuery.data?.deployments : undefined}
              onRangeSelected={setTimeFrame}
              loading={functionQuery.isLoading}
              timeFrame={timeFrame}
              firstSeenAt={functionQuery.data?.function.firstSeenAt}
            />
          </FunctionBehaviorChartContainer>
        </Row>
        {showExistsInFlow && (
          <FlowsByFunctionTable
            flows={functionQuery.data?.flows}
            directFlows={functionQuery.data?.directFlows}
            function={functionQuery.data?.function}
            loading={functionQuery.isLoading}
            referralBreadcrumbs={referralBreadcrumbs}
          />
        )}
        <InvocationSourcesTable
          type="callers"
          entities={functionQuery.data?.callers}
          function={functionQuery.data?.function}
          loading={functionQuery.isLoading}
          referralBreadcrumbs={referralBreadcrumbs}
          timeFrame={timeFrame}
        />
        <InvocationSourcesTable
          type="callees"
          entities={functionQuery.data?.callees}
          function={functionQuery.data?.function}
          loading={functionQuery.isLoading}
          referralBreadcrumbs={referralBreadcrumbs}
          timeFrame={timeFrame}
        />
        <ErrorsTable
          errors={functionQuery.data?.errors}
          function={functionQuery.data?.function}
          loading={functionQuery.isLoading}
        />
      </>
    </Page>
  );
}

